import React from 'react';
import { motion } from 'framer-motion';

const AgentsList = ({ selectedAgent, handleAgentSelect, agents, searchTerm, setSearchTerm, isReAssign }) => {
    return (
        <motion.div
            className="flex-1"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
        >
            <h3 className="text-lg font-semibold text-blue-500">
                Available Service Partners {isReAssign ? 'for Re-Assign' : ''}
            </h3>
            {isReAssign && (
                <div className="mt-2 p-2 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700">
                    <span className="font-bold">Current SP:</span> {isReAssign.agent.display_name}
                </div>
            )}
            <div className="bg-white rounded-lg shadow max-h-max flex flex-col">
                <div className="py-2 border-b">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search agents by name, email or mobile..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full px-4 py-2 pl-10 pr-4 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                    </div>
                </div>

                {/* Desktop Table View */}
                <div className="hidden md:block overflow-hidden border border-gray-300 rounded-lg">
                    <table className="min-w-full border-collapse">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-300">Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-300">Phone</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-300">Email</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-300">Distance</th>
                                {Array.from({ length: 3 }, (_, index) => {
                                    const date = new Date();
                                    date.setDate(date.getDate() + index + 1);
                                    return (
                                        <th key={index} className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-300">
                                            {date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {agents.map((agent, idx) => (
                                <motion.tr
                                    key={agent.id}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: idx * 0.05 }}
                                    className={`cursor-pointer ${agent.is_re ? 'bg-emerald-50 hover:bg-emerald-100' : 'hover:bg-gray-50'}`}
                                    onClick={() => handleAgentSelect(agent)}
                                >
                                    <td className="px-6 py-3 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={selectedAgent && selectedAgent.id === agent.id}
                                                onChange={() => handleAgentSelect(agent)}
                                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                            />
                                            <div className="ml-3">
                                                <div className="flex items-center">
                                                    <span className={`text-sm ${agent.is_re ? 're-name-shimmer font-semibold' : 'text-gray-900'}`}>{agent.full_name}</span>
                                                    {agent.is_re && (
                                                    <span className="ml-2 inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 border border-emerald-300">
                                                        <span className="material-icons text-xs">verified</span>
                                                        RE · Employee
                                                    </span>
                                                )}
                                                </div>
                                                {!isNaN(agent.worked_on_tickets_count) && (
                                                    <div className="flex items-center gap-1">
                                                        <span className="material-icons text-sm text-scogoorange">confirmation_number</span>
                                                        <span className="text-sm text-gray-500">{agent.worked_on_tickets_count} tickets</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{agent.mobile}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{agent.email}</td>
                                    {agent?.distance ? <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{`${Math.floor(parseFloat(agent?.distance) / 1000)} Km`}</td> : <></>}
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500"></td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500"></td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500"></td>
                                </motion.tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden space-y-3 p-4 max-h-96 overflow-y-auto">
                    {agents.map((agent, idx) => (
                        <motion.div
                            key={agent.id}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: idx * 0.05 }}
                            className={`p-4 rounded-lg border cursor-pointer transition-all duration-200 ${selectedAgent && selectedAgent.id === agent.id
                                ? 'border-blue-500 bg-blue-50'
                                : agent.is_re
                                    ? 'border-emerald-400 bg-emerald-50 hover:bg-emerald-100'
                                    : 'border-gray-200 bg-white hover:bg-gray-50'
                                }`}
                            onClick={() => handleAgentSelect(agent)}
                        >
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={selectedAgent && selectedAgent.id === agent.id}
                                        onChange={() => handleAgentSelect(agent)}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <span className={`ml-3 text-base font-medium ${agent.is_re ? 're-name-shimmer font-semibold' : 'text-gray-900'}`}>{agent.full_name}</span>
                                    {agent.is_re && (
                                                    <span className="ml-2 inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 border border-emerald-300">
                                                        <span className="material-icons text-xs">verified</span>
                                                        RE · Employee
                                                    </span>
                                                )}
                                </div>
                            </div>

                            <div className="space-y-2">
                                {agent.mobile && (
                                    <div className="flex items-center gap-2">
                                        <span className="text-gray-500 text-sm">Phone:</span>
                                        <span className="text-gray-700 text-sm font-medium">{agent.mobile}</span>
                                    </div>
                                )}

                                {agent.email && (
                                    <div className="flex items-center gap-2">
                                        <span className="text-gray-500 text-sm">Email:</span>
                                        <span className="text-gray-700 text-sm font-medium truncate">{agent.email}</span>
                                    </div>
                                )}

                                {agent?.distance && (
                                    <div className="flex items-center gap-2">
                                        <span className="text-gray-500 text-sm">Distance:</span>
                                        <span className="text-gray-700 text-sm font-medium">{`${Math.floor(parseFloat(agent?.distance) / 1000)} Km`}</span>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </motion.div>
    );
};

export default AgentsList;
